import React, { useEffect } from "react";
import {
  Button,
  DialogActions,
  DialogTitle,
  Spacer,
  TextField,
  Typography,
  useTheme,
} from "@ugc-ui/lego";
import { useStores } from "@client/store";
import { observer } from "mobx-react-lite";
import { Modal } from "@src/common/components/Modal/Modal";
import { ModalContentWrapper } from "@src/common/components/Modal/ModalTemplate/ModalContentWrapper/ModalContentWrapper";
import { FlexFieldsContainer } from "@src/client/pages/Home/InitialDialog/InitialDialog.styled";
import { useIsMobile } from "@src/common/hooks/isMobile";
import { useGlobalLoader } from "@common/hooks/useGlobalLoader";
import {
  isItnValid,
  isUrlValid,
  URLTextField,
} from "@src/common/components/URLTextField";
import { ErrorMessages } from "@common/consts/campaign";
import { ADVERTISER_LABEL } from "@common/consts/advertiser";

export const InitialDialog = observer(() => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  const {
    client: {
      initialDialogStore: {
        isFetching,
        isOpen,
        dialogData,
        setDialogData,
        updateAdvertiserData: handleSaveAndUpdate,
        attach,
      },
    },
  } = useStores();

  useGlobalLoader(isFetching);

  useEffect(() => {
    attach();
  }, [attach]);

  const handleChangeName = (e) => {
    const legalEntityName = e.target.value;

    setDialogData({ ...dialogData, legalEntityName });
  };

  const handleChangeItn = (e) => {
    const itn = e.target.value;

    setDialogData({ ...dialogData, itn: itn || undefined });
  };

  const handleChangeLink = (e) => {
    const legalEntityNameLink = e.target.value;

    setDialogData({ ...dialogData, legalEntityNameLink });
  };

  const isItnValidConst = !dialogData?.itn || isItnValid(dialogData?.itn);

  const isUrlValidConst =
    !dialogData?.legalEntityNameLink ||
    isUrlValid(dialogData?.legalEntityNameLink);

  return (
    <Modal
      isOpen={isOpen}
      dataCy="диалоговое окно Данные о рекламодателе"
      fullScreen={isMobile}
    >
      <ModalContentWrapper fullScreen={isMobile}>
        <DialogTitle>
          <Typography type="h5" color="primary">
            Данные о рекламодателе
          </Typography>
        </DialogTitle>
        <FlexFieldsContainer fullScreen={isMobile} gap={12}>
          <Typography color={theme.gray.a60} type="subtitle1">
            Введите данные своего юридического лица
          </Typography>
          <TextField
            value={dialogData?.legalEntityName}
            fullWidth
            onChange={handleChangeName}
            label={`${ADVERTISER_LABEL.legalEntityName} *`}
            error={!dialogData?.legalEntityName}
            helperText={
              !dialogData?.legalEntityName && ErrorMessages.REQUIRED_FIELD
            }
            dataCy="инпут-название юридического лица"
            maxLength={100}
          />
          <URLTextField
            value={dialogData?.legalEntityNameLink}
            fullWidth
            onChange={handleChangeLink}
            label={ADVERTISER_LABEL.legalEntityNameLink}
            dataCy="инпут-ссылка-на-сайт"
            error={!isUrlValidConst}
            maxLength={2048}
          />
          <TextField
            value={dialogData?.itn}
            fullWidth
            onChange={handleChangeItn}
            type="number"
            dataCy="инпут-инн"
            maxLength={12}
            error={!isItnValidConst}
            helperText={!isItnValidConst && ErrorMessages.INVALID_ITN}
            label={ADVERTISER_LABEL.itn}
          />
          <Typography color={theme.gray.a40}>
            Внесенные изменения отобразятся при создании новых рекламных
            кампаний.
            <br />
            Все ранее созданные рекламные кампании останутся со старыми данными.
          </Typography>
        </FlexFieldsContainer>
        <DialogActions>
          <Spacer size={40} />
          <Button
            fullWidth
            color="primary"
            theme={theme}
            onClick={handleSaveAndUpdate}
            disabled={
              !dialogData?.legalEntityName ||
              !isUrlValidConst ||
              !isItnValidConst
            }
          >
            Сохранить и продолжить
          </Button>
        </DialogActions>
      </ModalContentWrapper>
    </Modal>
  );
});
